import { Button, DropDown, ImageUpload, Input, useErrorState } from '@finbeat/react-toolkit';
import { getClientFile, updatePhoto } from 'axios/clientApi';
import FileSaver from 'file-saver';
import useApi from 'hooks/useApi';
import { UpdatePhotoInfo } from 'models/api/client';
import { RequestDocsInfo } from 'models/api/order';
import {
    Dispatch,
    Key,
    MouseEventHandler,
    RefObject,
    SetStateAction,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FaDownload, FaEdit, FaReply, FaShare } from 'react-icons/fa';
import { MdImageNotSupported } from 'react-icons/md';
import { useMutation, useQuery } from 'react-query';
import {
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    CardLink,
    FormGroup,
    Popover,
    PopoverBody,
} from 'reactstrap';
import '../OrderInfo.scss';

//TODO Рефакторинг
const ClientFile = ({
    canRequestingDocs,
    editable,
    header,
    loading,
    value,
    editReasons,
    fileType,
    modalRef,
    phoneNumber,
    docNum,
    clientId,
    orderId,
    setRequestedDocs,
}: {
    fileType: string;
    canRequestingDocs: boolean;
    editable: boolean;
    header: string;
    loading: boolean;
    value: number | null | undefined;
    editReasons?: DropDown[] | undefined;
    modalRef?: RefObject<HTMLElement>;
    phoneNumber: string | null;
    docNum: string | null;
    clientId: Key;
    orderId?: Key;
    setRequestedDocs?: Dispatch<SetStateAction<RequestDocsInfo[]>>;
}) => {
    const className = 'client-file';
    const { t } = useTranslation();
    const { handleRequestError } = useErrorState();
    const api = useApi();

    const ref = useRef<HTMLImageElement>(null);
    const popoverRef = useRef(null);

    const [popoverOpen, setPopoverOpen] = useState(false);
    const [igmSrc, setImgSrc] = useState<Blob>();
    const [rotate, setRotate] = useState(0);
    const [rerequestingDocument, setRerequestingDocument] = useState(false);
    const [docId, setDocId] = useState(value);
    useEffect(() => setDocId(value), [value]);

    const [editInfo, setEditInfo] = useState<UpdatePhotoInfo>({
        clientId: clientId.toString(),
        file: null,
        photoType: fileType,
        reason: useMemo(
            () => (editReasons && editReasons[0] && editReasons[0].name) ?? '',
            [editReasons]
        ),
        initPlace: orderId ? 'Orders' : 'Client',
        dsc: '',
        orderId: orderId?.toString() ?? '',
        phoneNumber: phoneNumber,
        docNum: docNum,
    });

    const toggle = () => setPopoverOpen((prev) => !prev);

    useEffect(() => {
        setRequestedDocs &&
            setRequestedDocs((prev) =>
                rerequestingDocument
                    ? [
                          ...prev,
                          {
                              fileType: fileType,
                          },
                      ]
                    : prev.filter((x) => x.fileType !== fileType)
            );
    }, [rerequestingDocument]);

    const updatePhotoMutation = useMutation(
        'updateClientPhotoMutation',
        () => {
            const formData = new FormData();
            Object.entries(editInfo).map(([key, value]) => value && formData.append(key, value));
            return updatePhoto(api, formData);
        },
        {
            onSuccess: (data) => {
                setImgSrc(undefined);
                setDocId(data);
                toggle();
            },
            onError: (e) => handleRequestError(e),
        }
    );

    const clientFileQuery = useQuery(
        ['clientFile', docId],
        () => (docId ? getClientFile(api, docId) : new Blob()),
        {
            onSuccess: (data) => {
                setImgSrc(data);
            },
            enabled: !!docId && !igmSrc,
        }
    );

    const src = useMemo(
        () => (igmSrc ? URL.createObjectURL(new Blob([igmSrc], { type: 'image/jpeg' })) : ''),
        [igmSrc]
    );
    const [isBackgroundImageActive, setBackgroundImageActive] = useState(false);

    const height = useMemo(() => {
        if (!ref.current) return '';
        switch (Math.abs(rotate)) {
            case 90:
            case 270:
            case -90:
            case -270:
                return `${ref.current.clientWidth + 32}px`;
            default:
                return '';
        }
    }, [rotate]);

    const top = useMemo(() => {
        if (!ref.current) return '';
        switch (Math.abs(rotate)) {
            case 90:
            case 270:
            case -90:
            case -270:
                return `${(ref.current.clientWidth - ref.current.clientHeight) / 2}px`;
            default:
                return '';
        }
    }, [rotate]);

    const [backgroundPosition, setBackgroundPosition] = useState('0% 0%');

    const handleMouseMove: MouseEventHandler<HTMLDivElement> = (e) => {
        const { left, right, top, bottom, width, height } = (
            e.target as HTMLDivElement
        ).getBoundingClientRect();
        let vars = { dividendX: 0, dividendY: 0, dividerX: 0, dividerY: 0 };
        switch (rotate) {
            case 90:
            case -270:
                vars = {
                    dividendX: e.pageY - top,
                    dividendY: right - e.pageX,
                    dividerX: height,
                    dividerY: width,
                };
                break;
            case 180:
            case -180:
                vars = {
                    dividendX: right - e.pageX,
                    dividendY: bottom - e.pageY,
                    dividerX: width,
                    dividerY: height,
                };
                break;
            case 270:
            case -90:
                vars = {
                    dividendX: bottom - e.pageY,
                    dividendY: e.pageX - left,
                    dividerX: height,
                    dividerY: width,
                };
                break;
            default:
                vars = {
                    dividendX: e.pageX - left,
                    dividendY: e.pageY - top,
                    dividerX: width,
                    dividerY: height,
                };
                break;
        }

        const x = (vars.dividendX / vars.dividerX) * 100;
        const y = (vars.dividendY / vars.dividerY) * 100;

        setBackgroundPosition(`${x}% ${y}%`);
    };

    return (
        <Card className={className}>
            <CardHeader>
                {canRequestingDocs ? (
                    <Input
                        type="checkbox"
                        label={header}
                        checked={rerequestingDocument}
                        className="mb-3"
                        labelClassName="h4"
                        inputClassName={`${className}__doc-upd-checkbox`}
                        onChange={(e) => setRerequestingDocument(e.target.checked)}
                    />
                ) : (
                    <h4>{header}</h4>
                )}
            </CardHeader>
            <CardBody className={`${className}__body`} style={{ height: height }}>
                {loading || clientFileQuery.isFetching ? (
                    <div className={`loading ${className}__loading`}></div>
                ) : !src ? (
                    <div className={`${className}__img_not-found`}>
                        <MdImageNotSupported />
                    </div>
                ) : (
                    <div>
                        <div
                            className={`${className}__img-container`}
                            onMouseMove={handleMouseMove}
                            onMouseEnter={() => setBackgroundImageActive(true)}
                            onMouseLeave={() => setBackgroundImageActive(false)}
                            style={{
                                backgroundPosition: backgroundPosition,
                                backgroundImage: isBackgroundImageActive ? `url(${src})` : 'none',
                                transform: `rotate(${rotate}deg)`,
                                top: top,
                            }}
                        >
                            <img
                                ref={ref}
                                alt={t('BackOfficeLogoNotFound')}
                                width="100%"
                                height="100%"
                                className={`${className}__img card-img`}
                                src={src}
                            />
                        </div>
                    </div>
                )}
            </CardBody>
            <CardFooter className={`${className}__footer`}>
                <div>
                    <CardLink>
                        <FaDownload
                            onClick={() => {
                                igmSrc && FileSaver.saveAs(new Blob([igmSrc]), `${header}.jpg`);
                            }}
                        />
                    </CardLink>
                    {editable && (
                        <>
                            <CardLink innerRef={popoverRef}>
                                <FaEdit />
                            </CardLink>
                            <Popover
                                target={popoverRef}
                                placement="left"
                                trigger="legacy"
                                container={modalRef ?? popoverRef}
                                isOpen={popoverOpen}
                                toggle={toggle}
                            >
                                <PopoverBody>
                                    <FormGroup>
                                        <ImageUpload
                                            canUpload={false}
                                            alt={t('BackOfficeLogoNotFound')}
                                            onChange={(f) =>
                                                setEditInfo((prev) => ({
                                                    ...prev,
                                                    file: f,
                                                }))
                                            }
                                            label={editInfo.file?.name}
                                            showPreview={false}
                                        />
                                    </FormGroup>

                                    <Input
                                        id="info-reason-field"
                                        type="select"
                                        value={editInfo.reason}
                                        options={
                                            editReasons &&
                                            editReasons.map(({ id, name }) => (
                                                <option key={id} value={id ?? undefined}>
                                                    {name}
                                                </option>
                                            ))
                                        }
                                        name="reason"
                                        onChange={(e) =>
                                            setEditInfo((prev) => ({
                                                ...prev,
                                                reason: e.target.value,
                                            }))
                                        }
                                        label={t('BackOfficeReason')}
                                    />
                                    <Input
                                        value={editInfo.dsc}
                                        type="textarea"
                                        onChange={(e) =>
                                            setEditInfo((prev) => ({
                                                ...prev,
                                                dsc: e.target.value,
                                            }))
                                        }
                                        label={t('BackOfficeComment')}
                                    />
                                    <div>
                                        <Button
                                            color="success"
                                            className="float-end"
                                            onClick={() => {
                                                updatePhotoMutation.mutate();
                                            }}
                                            isPending={updatePhotoMutation.isLoading}
                                        >
                                            <Trans i18nKey="BackOfficeOk" />
                                        </Button>
                                        <Button color="secondary" onClick={toggle}>
                                            <Trans i18nKey="BackOfficeCancel" />
                                        </Button>
                                    </div>
                                </PopoverBody>
                            </Popover>
                        </>
                    )}
                </div>
                <div>
                    <CardLink>
                        <FaReply
                            onClick={() => setRotate((prev) => (prev === -270 ? 0 : prev - 90))}
                        />
                    </CardLink>
                    <CardLink>
                        <FaShare
                            onClick={() => setRotate((prev) => (prev === 270 ? 0 : prev + 90))}
                        />
                    </CardLink>
                </div>
            </CardFooter>
        </Card>
    );
};

export default ClientFile;
